import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_green-creations/artifacts/cf3sam7y_Untitled%20design.png";

const coffeeHutImages = [
  "https://customer-assets.emergentagent.com/job_green-creations/artifacts/ze3k8y7t_coffee%20hut%20.png",
  "https://customer-assets.emergentagent.com/job_green-creations/artifacts/3g2ntap8_coffee%20hut%201.png",
  "https://customer-assets.emergentagent.com/job_green-creations/artifacts/5qfnbwi9_coffee%20hut%202.png",
  "https://customer-assets.emergentagent.com/job_green-creations/artifacts/exqe5rw7_coffee%20hut%203.png",
  "https://customer-assets.emergentagent.com/job_green-creations/artifacts/qkg43nv0_coffee%20hut%204.png",
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.6, ease: "easeOut" },
  }),
};

export default function Conversions() {
  return (
    <div data-testid="conversions-page">
      {/* Page Header */}
      <section className="py-20 md:py-28 bg-[#EAE5D9]" data-testid="conversions-page-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row md:items-center md:justify-between gap-8"
          >
            <div className="flex-1">
              <p className="text-[#8B5E34] font-body text-sm tracking-widest uppercase mb-4">
                Collection
              </p>
              <h1 className="font-heading text-5xl sm:text-7xl tracking-tighter font-light text-[#222522]">
                Conversions
              </h1>
              <p className="font-body text-base md:text-lg text-[#5C635B] mt-6 max-w-2xl leading-relaxed">
                From horseboxes to buses, and sometimes just a bare chassis with wheels — 
                if it rolls, we can probably turn it into something brilliant. We&apos;ve converted 
                horse trailers into shower blocks, built coffee huts from scratch on a towbar, 
                and we&apos;re always up for the next daft challenge.
              </p>
            </div>
            <img
              src={LOGO_URL}
              alt="Small Feet"
              className="h-28 md:h-36 w-auto object-contain rounded flex-shrink-0"
              style={{ mixBlendMode: "multiply" }}
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex gap-4 mt-8"
          >
            <a
              href="https://www.ebay.co.uk/usr/small_feet"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#3A5A3F] text-[#F9F8F5] text-sm font-body tracking-wider uppercase hover:bg-[#2d4832] transition-all hover:translate-y-[-1px] hover:shadow-lg"
              data-testid="shop-ebay-link"
            >
              Shop on eBay
              <ExternalLink size={14} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Mash Tun Bus Feature Section */}
      <section className="py-24 md:py-32" data-testid="mash-tun-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            custom={0}
            variants={fadeUp}
            className="mb-10"
          >
            <p className="text-[#8B5E34] font-body text-sm tracking-widest uppercase mb-3">
              Our Biggest Build
            </p>
            <h2 className="font-heading text-3xl sm:text-5xl tracking-tight font-light text-[#222522]">
              The Mash Tun Bus
            </h2>
            <p className="font-body text-base text-[#5C635B] mt-5 max-w-2xl leading-relaxed">
              A full-size double decker passenger bus transformed into a rustic cafe — 
              classic Small Feet style. She started life as the S20 Roberts service bus, 
              complete with blue seats, chrome rails, and that unmistakable bus smell. 
              We ripped the lot out and rebuilt the interior with reclaimed timber, 
              salvaged materials, and a hefty dose of character. Passengers out, cuppas in. 
              More photos coming soon as we show the full transformation.
            </p>
          </motion.div>

          {/* Before Photos */}
          <div className="mb-6">
            <p className="text-[#8B5E34] font-body text-xs tracking-widest uppercase mb-4">
              Before — The Starting Point
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={0}
              variants={fadeUp}
              className="md:col-span-5"
            >
              <div className="gallery-item h-80 md:h-[30rem] relative rounded overflow-hidden" data-testid="mash-tun-before-exterior">
                <img
                  src="https://customer-assets.emergentagent.com/job_green-creations/artifacts/xan08yvb_Screenshot%202026-04-19%20at%2012.39.53.png"
                  alt="The Mash Tun Bus — before, exterior S20 Roberts"
                  className="w-full h-full object-cover"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Before — Exterior</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">The S20 Roberts — a proper double decker in its previous life</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={1}
              variants={fadeUp}
              className="md:col-span-7"
            >
              <div className="grid grid-rows-2 gap-6 h-80 md:h-[30rem]">
                <div className="gallery-item relative rounded overflow-hidden" data-testid="mash-tun-before-lower">
                  <img
                    src="https://customer-assets.emergentagent.com/job_green-creations/artifacts/mcew9bsq_Screenshot%202026-04-19%20at%2012.40.03.png"
                    alt="The Mash Tun Bus — before, lower deck interior"
                    className="w-full h-full object-cover"
                  />
                  <div className="overlay">
                    <div>
                      <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Before — Lower Deck</h3>
                      <p className="font-body text-sm text-[#F9F8F5]/70">Chrome rails and bus seats — all had to go</p>
                    </div>
                  </div>
                </div>
                <div className="gallery-item relative rounded overflow-hidden" data-testid="mash-tun-before-upper">
                  <img
                    src="https://customer-assets.emergentagent.com/job_green-creations/artifacts/xlixllbx_Screenshot%202026-04-19%20at%2012.40.11.png"
                    alt="The Mash Tun Bus — before, upper deck interior"
                    className="w-full h-full object-cover"
                  />
                  <div className="overlay">
                    <div>
                      <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Before — Upper Deck</h3>
                      <p className="font-body text-sm text-[#F9F8F5]/70">Rows of passenger seats ready for the skip</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Conversion Photos */}
          <div className="mt-12 mb-6">
            <p className="text-[#8B5E34] font-body text-xs tracking-widest uppercase mb-4">
              The Conversion — Getting Stuck In
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={0}
              variants={fadeUp}
              className="md:col-span-4"
            >
              <div className="gallery-item h-72 md:h-96 relative rounded overflow-hidden" data-testid="mash-tun-strip">
                <img
                  src="https://customer-assets.emergentagent.com/job_green-creations/artifacts/kh4m7k1e_Screenshot%202026-04-19%20at%2012.47.26.png"
                  alt="Stripping out the old seats"
                  className="w-full h-full object-cover object-center"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Stripping Out</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Ripping out the old seats and fittings</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={1}
              variants={fadeUp}
              className="md:col-span-4"
            >
              <div className="gallery-item h-72 md:h-96 relative rounded overflow-hidden" data-testid="mash-tun-shell">
                <img
                  src="https://customer-assets.emergentagent.com/job_green-creations/artifacts/kp2fdevi_Screenshot%202026-04-19%20at%2012.47.36.png"
                  alt="Bus stripped to shell in the workshop"
                  className="w-full h-full object-cover object-center"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Back to the Shell</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Windows out, panels off — starting from scratch</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={2}
              variants={fadeUp}
              className="md:col-span-4"
            >
              <div className="gallery-item h-72 md:h-96 relative rounded overflow-hidden" data-testid="mash-tun-cladding">
                <img
                  src="https://customer-assets.emergentagent.com/job_green-creations/artifacts/go7ju8xa_Screenshot%202026-04-19%20at%2012.47.53.png"
                  alt="Reclaimed cladding going in through the windows"
                  className="w-full h-full object-cover object-center"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Cladding Up</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Reclaimed cladding through the window frames</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={3}
              variants={fadeUp}
              className="md:col-span-6"
            >
              <div className="gallery-item h-72 md:h-96 relative rounded overflow-hidden" data-testid="mash-tun-sparks">
                <img
                  src="https://customer-assets.emergentagent.com/job_green-creations/artifacts/xkbplkh9_Screenshot%202026-04-19%20at%2012.48.04.png"
                  alt="Grinding and metalwork on the lower deck"
                  className="w-full h-full object-cover object-center"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Sparks Flying</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Grinding, welding, and graft on the lower deck</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={4}
              variants={fadeUp}
              className="md:col-span-6"
            >
              <div className="gallery-item h-72 md:h-96 relative rounded overflow-hidden" data-testid="mash-tun-timber">
                <img
                  src="https://customer-assets.emergentagent.com/job_green-creations/artifacts/w3v5hnkd_Screenshot%202026-04-19%20at%2012.48.34.png"
                  alt="Reclaimed timber cladding the walls"
                  className="w-full h-full object-cover object-center"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Timber Going In</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Reclaimed timber cladding the walls — starting to look like a cafe</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mash Tun — The Finished Product */}
      <section className="py-24 md:py-32 bg-[#EAE5D9]" data-testid="mash-tun-finished-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-6">
            <p className="text-[#8B5E34] font-body text-xs tracking-widest uppercase mb-4">
              The Finished Product — Passengers Out, Cuppas In
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={0}
              variants={fadeUp}
              className="md:col-span-7"
            >
              <div className="gallery-item h-80 md:h-[30rem] relative rounded overflow-hidden" data-testid="mash-tun-finished-exterior">
                <img
                  src="https://customer-assets.emergentagent.com/job_green-creations/artifacts/odknfkm8_Screenshot%202026-04-19%20at%2012.52.40.png"
                  alt="The Mash Tun Bus — finished exterior with keg tables"
                  className="w-full h-full object-cover object-center"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">The Mash Tun</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Open for business — keg tables, chalkboard menus, the lot</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={1}
              variants={fadeUp}
              className="md:col-span-5"
            >
              <div className="gallery-item h-80 md:h-[30rem] relative rounded overflow-hidden" data-testid="mash-tun-finished-lower">
                <img
                  src="https://customer-assets.emergentagent.com/job_green-creations/artifacts/87j19af1_Screenshot%202026-04-19%20at%2012.50.57.png"
                  alt="The Mash Tun Bus — lower deck cafe interior"
                  className="w-full h-full object-cover object-center"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Lower Deck Cafe</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Reclaimed timber tables, coffee sack chairs, bunting — proper cosy</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={2}
              variants={fadeUp}
              className="md:col-span-4"
            >
              <div className="gallery-item h-72 md:h-96 relative rounded overflow-hidden" data-testid="mash-tun-finished-stairwell">
                <img
                  src="https://customer-assets.emergentagent.com/job_green-creations/artifacts/w2ntsda5_Screenshot%202026-04-19%20at%2012.55.13.png"
                  alt="The Mash Tun Bus — stairwell with newspaper-lined walls"
                  className="w-full h-full object-cover object-center"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">The Stairwell</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Newspaper-lined walls and reclaimed timber — proper character</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={3}
              variants={fadeUp}
              className="md:col-span-4"
            >
              <div className="gallery-item h-72 md:h-96 relative rounded overflow-hidden" data-testid="mash-tun-finished-door">
                <img
                  src="https://customer-assets.emergentagent.com/job_green-creations/artifacts/qb8d4piz_Screenshot%202026-04-19%20at%2012.55.22.png"
                  alt="The Mash Tun Bus — reclaimed door and stairway"
                  className="w-full h-full object-cover object-center"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Mind Your Head</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Reclaimed door, original bus stairway, timber cladding</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={4}
              variants={fadeUp}
              className="md:col-span-4"
            >
              <div className="gallery-item h-72 md:h-96 relative rounded overflow-hidden" data-testid="mash-tun-finished-sunset">
                <img
                  src="https://customer-assets.emergentagent.com/job_green-creations/artifacts/yh0fmsdf_Screenshot%202026-04-19%20at%2012.55.37.png"
                  alt="The Mash Tun Bus — golden hour with serving hatch open"
                  className="w-full h-full object-cover object-center"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Golden Hour</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Serving hatch open, tables out — doesn&apos;t get better than this</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Coffee Hut Feature Section */}
      <section className="py-24 md:py-32" data-testid="coffee-hut-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            custom={0}
            variants={fadeUp}
            className="mb-10"
          >
            <p className="text-[#8B5E34] font-body text-sm tracking-widest uppercase mb-3">
              Featured Build
            </p>
            <h2 className="font-heading text-3xl sm:text-5xl tracking-tight font-light text-[#222522]">
              The Coffee Hut
            </h2>
            <p className="font-body text-base text-[#5C635B] mt-5 max-w-2xl leading-relaxed">
              Started with nothing but a bare chassis and a brilliant idea. We clad the whole 
              thing in reclaimed featherboard, fitted a salvaged door as the entrance, and 
              added a hinged serving hatch that flips up when it&apos;s time for business. 
              Reclaimed windows, corrugated roof, towable chassis — it rocks up to 
              events, markets, and festivals looking like it&apos;s been there forever. 
              Flat whites never tasted so rustic.
            </p>
          </motion.div>

          {/* Coffee Hut Image Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            {/* Main hero — serving hatch view */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={0}
              variants={fadeUp}
              className="md:col-span-8"
            >
              <div className="gallery-item h-80 md:h-[30rem] relative rounded overflow-hidden" data-testid="coffee-hut-hero">
                <img
                  src={coffeeHutImages[2]}
                  alt="The Coffee Hut — serving hatch and side view"
                  className="w-full h-full object-cover"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Serving Hatch View</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Hinged hatch open for serving, reclaimed featherboard cladding</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Door entrance view */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={1}
              variants={fadeUp}
              className="md:col-span-4"
            >
              <div className="gallery-item h-80 md:h-[30rem] relative rounded overflow-hidden" data-testid="coffee-hut-door">
                <img
                  src={coffeeHutImages[0]}
                  alt="The Coffee Hut — reclaimed door entrance"
                  className="w-full h-full object-cover"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Reclaimed Door</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Original reclaimed door with character</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Front with hatch + door */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={2}
              variants={fadeUp}
              className="md:col-span-5"
            >
              <div className="gallery-item h-72 md:h-96 relative rounded overflow-hidden" data-testid="coffee-hut-front">
                <img
                  src={coffeeHutImages[3]}
                  alt="The Coffee Hut — front view with hatch open"
                  className="w-full h-full object-cover"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Front View</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Door and serving hatch side by side</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Rear / tow end */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={3}
              variants={fadeUp}
              className="md:col-span-7"
            >
              <div className="gallery-item h-72 md:h-96 relative rounded overflow-hidden" data-testid="coffee-hut-rear">
                <img
                  src={coffeeHutImages[1]}
                  alt="The Coffee Hut — rear tow view"
                  className="w-full h-full object-cover"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Tow End</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Towable chassis with hinged serving awning</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Landscape / setting shot */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={4}
              variants={fadeUp}
              className="md:col-span-12"
            >
              <div className="gallery-item h-72 md:h-[26rem] relative rounded overflow-hidden" data-testid="coffee-hut-landscape">
                <img
                  src={coffeeHutImages[4]}
                  alt="The Coffee Hut — in its countryside setting"
                  className="w-full h-full object-cover"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">In the Countryside</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Built from the chassis up — ready to go anywhere</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Shower Trailer Feature Section */}
      <section className="py-24 md:py-32 bg-[#EAE5D9]" data-testid="shower-trailer-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            custom={0}
            variants={fadeUp}
            className="mb-10"
          >
            <p className="text-[#8B5E34] font-body text-sm tracking-widest uppercase mb-3">
              Featured Build
            </p>
            <h2 className="font-heading text-3xl sm:text-5xl tracking-tight font-light text-[#222522]">
              The Shower Trailer
            </h2>
            <p className="font-body text-base text-[#5C635B] mt-5 max-w-2xl leading-relaxed">
              &ldquo;Can you turn a horse trailer into a shower block?&rdquo; — Sure, why not. 
              We stripped it back to the bare shell, built a timber stud framework inside, 
              lined it with ply, plumbed in proper shower trays, and created individual 
              cubicles. Horses out, hot water in. Now it does the rounds at festivals 
              and glamping sites. The horses haven&apos;t complained.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={0}
              variants={fadeUp}
              className="md:col-span-12"
            >
              <div className="gallery-item h-80 md:h-[32rem] relative rounded overflow-hidden" data-testid="shower-trailer-hero">
                <img
                  src="https://customer-assets.emergentagent.com/job_green-creations/artifacts/yin9yrjz_shower%20trailer.png"
                  alt="Shower Trailer — stripped back horse trailer ready for conversion"
                  className="w-full h-full object-cover"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">The Starting Point</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Horse trailer stripped back to the shell, ready for conversion</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={1}
              variants={fadeUp}
              className="md:col-span-4"
            >
              <div className="gallery-item h-72 md:h-[26rem] relative rounded overflow-hidden" data-testid="shower-trailer-cubicle">
                <img
                  src="https://customer-assets.emergentagent.com/job_green-creations/artifacts/voiym2hj_shower%20trailer%201.png"
                  alt="Shower Trailer — shower cubicle with tray fitted"
                  className="w-full h-full object-cover"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Shower Cubicle</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Ply-lined walls with shower tray fitted</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={2}
              variants={fadeUp}
              className="md:col-span-8"
            >
              <div className="gallery-item h-72 md:h-[26rem] relative rounded overflow-hidden" data-testid="shower-trailer-frame">
                <img
                  src="https://customer-assets.emergentagent.com/job_green-creations/artifacts/kshbiyif_shower%20trailer%202.png"
                  alt="Shower Trailer — timber stud framework inside"
                  className="w-full h-full object-cover"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Framework Build</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Timber stud partition walls taking shape</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={3}
              variants={fadeUp}
              className="md:col-span-7"
            >
              <div className="gallery-item h-72 md:h-96 relative rounded overflow-hidden" data-testid="shower-trailer-interior">
                <img
                  src="https://customer-assets.emergentagent.com/job_green-creations/artifacts/cfdnxqdu_shower%20trsiler%203.png"
                  alt="Shower Trailer — interior build progress"
                  className="w-full h-full object-cover"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Interior Progress</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Ply lining and stud walls mid-build</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              custom={4}
              variants={fadeUp}
              className="md:col-span-5"
            >
              <div className="gallery-item h-72 md:h-96 relative rounded overflow-hidden" data-testid="shower-trailer-rear">
                <img
                  src="https://customer-assets.emergentagent.com/job_green-creations/artifacts/nsficbps_shower%20trailer%204.png"
                  alt="Shower Trailer — rear view with cubicle framing"
                  className="w-full h-full object-cover"
                />
                <div className="overlay">
                  <div>
                    <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">Rear View</h3>
                    <p className="font-body text-sm text-[#F9F8F5]/70">Cubicle framing and shower tray from the back</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

    </div>
  );
}
